import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const MODULE_KEYS = [
  'dashboard',
  'crm',
  'propostas',
  'modelos-tributarios',
  'servicos',
  'metas',
  'gestao-rh',
  'setores',
  'avaliacao-ai',
  'clientes',
  'imposto-renda',
  'tarefas-gclick',
  'fechamentos-contabeis',
  'gob-federal',
  'gob-caixa-postal',
  'gob-trabalhista',
  'gob-fgts',
  'gob-estadual',
  'gob-cobertura',
  'configuracoes',
  'ver-valores-ir',
  'portal-colaborador',
  'portal-lider',
  'honorarios',
  'melhorias',
  'telefonia',
  'produtividade',
  'confisbil',
  'ferramentas',
  'carga-setor',
  'financeiro',
  'validador-pdf',
  'enquetes',
  'reforma-tributaria',
  'fiscal',
  'minha-carteira',
  'certificado-digital',
  'avaliacao-cliente',
  'novidades',
  'sucesso-cliente',
  'painel-setores',
  'raio-x-cliente',
  'solicitacoes-clientes',
  'balancete-cadima',
  'gerador-documentos',
  'controle-alvara',
  'ativos-ti',
  'integracoes-contabilidade',
  'conciliacao-bancaria',
  'ir-rendimentos',
  'notas-servico',
  'alteracoes-contratuais',
  'sugestoes',
  'painel-gestao',
  'fluxo-onboarding',
  'comissoes-diretoria',
  'modulo-tarefas',
  'modulo-tarefas-config',
  'eugenio',
  'eugenio-dominio',
  'eugenio-ensinar',
  'eugenio-curadoria',
] as const;


export type ModuleKey = typeof MODULE_KEYS[number];

export const ROUTE_MODULE_MAP: Record<string, ModuleKey> = {
  '/dashboard': 'dashboard',
  '/crm': 'crm',
  '/propostas': 'propostas',
  '/propostas/nova': 'propostas',
  '/modelos-tributarios': 'modelos-tributarios',
  '/servicos': 'servicos',
  '/metas': 'metas',
  '/gestao-rh': 'gestao-rh',
  '/admin/carga-setor': 'carga-setor',
  '/avaliacao-ai': 'avaliacao-ai',
  '/setores': 'setores',
  '/clientes': 'clientes',
  '/comunicados-clientes': 'clientes',
  '/acessos-portal-cliente': 'clientes',
  '/analise-cliente': 'clientes',
  '/avaliacao-cliente': 'avaliacao-cliente',
  '/sucesso-cliente': 'sucesso-cliente',
  '/sucesso-cliente/fluxo-onboarding': 'fluxo-onboarding',
  '/comissoes/politica': 'comissoes-diretoria',
  '/modulo-tarefas/perfil': 'modulo-tarefas',
  '/modulo-tarefas/enquete': 'modulo-tarefas-config',
  '/modulo-tarefas/catalogo': 'modulo-tarefas-config',
  '/modulo-tarefas/pessoas': 'modulo-tarefas-config',
  '/modulo-tarefas/departamentos': 'modulo-tarefas-config',
  '/raio-x-cliente': 'raio-x-cliente',
  '/solicitacoes-clientes': 'solicitacoes-clientes',
  '/balancete-cadima': 'balancete-cadima',
  '/gerador-documentos': 'gerador-documentos',
  '/ativos-ti': 'ativos-ti',
  '/alteracoes-contratuais': 'alteracoes-contratuais',
  '/painel-setores': 'painel-setores',
  '/admin/novidades': 'novidades',
  '/imposto-renda': 'imposto-renda',
  '/contas-receber': 'financeiro',
  '/contas-pagar': 'financeiro',
  '/plano-contas-despesas': 'financeiro',
  '/validador-financeiro': 'financeiro',
  '/tarefas-gclick': 'tarefas-gclick',
  '/fechamentos-contabeis': 'fechamentos-contabeis',
  '/certificado-digital': 'certificado-digital',
  '/integracao-gob/cnd-federal': 'gob-federal',
  '/integracao-gob/caixa-postal-ecac': 'gob-caixa-postal',
  '/integracao-gob/cnd-trabalhista': 'gob-trabalhista',
  '/integracao-gob/crf-fgts': 'gob-fgts',
  '/integracao-gob/cnd-estadual': 'gob-estadual',
  '/integracao-gob/cobertura': 'gob-cobertura',
  '/integracao-gob/controle-alvara': 'controle-alvara',
  '/honorarios': 'honorarios',
  '/configuracoes': 'configuracoes',
  '/melhorias': 'melhorias',
  '/telefonia': 'telefonia',
  '/produtividade': 'produtividade',
  '/confisbil': 'confisbil',
  '/ferramentas': 'ferramentas',
  '/validador-pdf': 'validador-pdf',
  '/integracoes/contabilidade/extratos-ofx': 'integracoes-contabilidade',
  '/integracoes/contabilidade/conciliacao-bancaria': 'conciliacao-bancaria',
  '/integracoes/contabilidade/ir-rendimentos': 'ir-rendimentos',
  '/notas-servico': 'notas-servico',
  '/enquetes-rh': 'enquetes',
  '/reforma-tributaria': 'reforma-tributaria',
  '/fiscal': 'fiscal',
  '/reforma-tributaria/viabilidade-simples': 'reforma-tributaria',
  '/portal-colaborador/enquetes': 'portal-colaborador',
  '/portal-lider/enquetes': 'portal-lider',
  '/portal-lider/minha-carteira': 'minha-carteira',
  '/sugestoes': 'sugestoes',
  '/admin/painel-gestao': 'painel-gestao',
  '/portal-lider/painel-gestao': 'painel-gestao',
  '/eugenio-aprendizado': 'eugenio-curadoria',
  '/eugenio': 'eugenio',
};

export function getModuleForPath(pathname: string): ModuleKey | undefined {
  if (ROUTE_MODULE_MAP[pathname]) return ROUTE_MODULE_MAP[pathname];
  return Object.entries(ROUTE_MODULE_MAP)
    .sort((a, b) => b[0].length - a[0].length)
    .find(([path]) => pathname.startsWith(`${path}/`))?.[1];
}

export const MODULE_LABELS: Record<ModuleKey, string> = {
  dashboard: 'Dashboard',
  crm: 'CRM Comercial',
  propostas: 'Propostas',
  'modelos-tributarios': 'Modelos Tributários',
  servicos: 'Serviços',
  metas: 'Metas',
  'gestao-rh': 'Gestão RH',
  setores: 'Setores',
  'avaliacao-ai': 'Avaliação AI',
  clientes: 'Clientes',
  'imposto-renda': 'Imposto de Renda',
  'tarefas-gclick': 'Tarefas GClick',
  'fechamentos-contabeis': 'Fechamentos Contábeis',
  'gob-federal': 'GOB — Regularidade Federal',
  'gob-caixa-postal': 'GOB — Caixa Postal e-CAC',
  'gob-trabalhista': 'GOB — Certidão Trabalhista',
  'gob-fgts': 'GOB — Regularidade FGTS',
  'gob-estadual': 'GOB — Certidão Estadual',
  'gob-cobertura': 'GOB — Cobertura',
  configuracoes: 'Configurações',
  'ver-valores-ir': 'Ver Valores IR',
  'portal-colaborador': 'Portal Colaborador',
  'portal-lider': 'Portal Líder',
  'honorarios': 'Honorários',
  'melhorias': 'Tickets/Melhorias',
  'telefonia': 'Telefonia (Yeastar)',
  'produtividade': 'Produtividade (WorkMonitor)',
  'confisbil': 'Confisbil',
  'ferramentas': 'Ferramentas',
  'carga-setor': 'Carga do Setor',
  'financeiro': 'Financeiro',
  'validador-pdf': 'Validador de PDFs',
  'enquetes': 'Enquetes RH',
  'reforma-tributaria': 'Reforma Tributária',
  'fiscal': 'Fiscal (Validador de Empresas)',
  'minha-carteira': 'Minha Carteira',
  'certificado-digital': 'Certificado Digital',
  'avaliacao-cliente': 'Avaliação do Cliente',
  'novidades': 'Novidades e Avisos (gestão)',
  'sucesso-cliente': 'Sucesso do Cliente',
  'painel-setores': 'Painel Semanal de Setores',
  'raio-x-cliente': 'Raio X do Cliente',
  'solicitacoes-clientes': 'Solicitações a Clientes',
  'balancete-cadima': 'Balancete QC Cadima',
  'gerador-documentos': 'Gerador de Documentos',
  'controle-alvara': 'Controle de Alvará',
  'ativos-ti': 'Ativos de TI',
  'integracoes-contabilidade': 'Extratos PDF → OFX (Integrações — Contabilidade)',
  'conciliacao-bancaria': 'Conciliação Bancária (Integrações — Contabilidade)',
  'ir-rendimentos': 'IR sobre Rendimentos (Contabilidade)',
  'notas-servico': 'Notas de Serviço — NFS-e pelo ADN (Fiscal)',
  'alteracoes-contratuais': 'Alterações Contratuais',
  'sugestoes': 'Sugestões dos Colaboradores',
  'painel-gestao': 'Painel de Gestão',
  'fluxo-onboarding': 'Fluxo de Onboarding (lançamentos)',
  'comissoes-diretoria': 'Comissões do Comercial (diretoria)',
  'modulo-tarefas': 'Módulo de Tarefas (consulta)',
  'modulo-tarefas-config': 'Módulo de Tarefas (configuração)',
  // "Em breve" até a fase do briefing de 30/10/2026 que liga a chave: Domínio na
  // fase 3. Liberar antes não abre nada.
  'eugenio': 'Eugênio IA',
  'eugenio-dominio': 'Eugênio IA: consultar o Domínio',
  'eugenio-ensinar': 'Eugênio IA: ensinar sem aprovação',
  'eugenio-curadoria': 'Eugênio IA: revisar a base de conhecimento',
};


// ============================================================
// Cache singleton: 3+ consumers (Layout, ProtectedRoute, Sidebar...)
// chamavam essa query a cada navegação. Agora um único fetch
// por usuário é compartilhado, com TTL de 5 min.
// ============================================================

interface CacheEntry {
  ts: number;
  permissions: Record<string, boolean>;
  explicit: Record<string, boolean>;
}

const PERM_TTL_MS = 5 * 60 * 1000;
const permCache = new Map<string, CacheEntry>();
const permInflight = new Map<string, Promise<CacheEntry>>();
type PermListener = (entry: CacheEntry) => void;
const permListeners = new Map<string, Set<PermListener>>();

async function loadPermissions(userId: string): Promise<CacheEntry> {
  const existing = permInflight.get(userId);
  if (existing) return existing;

  const p = (async () => {
    const { data, error } = await (supabase as any)
      .from('user_module_permissions')
      .select('module, enabled')
      .eq('user_id', userId);

    const map: Record<string, boolean> = {};
    const explicit: Record<string, boolean> = {};
    MODULE_KEYS.forEach(k => { map[k] = false; });
    data?.forEach((row: any) => { map[row.module] = row.enabled; explicit[row.module] = row.enabled; });

    if (error) {
      const checks = await Promise.all(
        MODULE_KEYS.map(async (module) => {
          const { data: allowed } = await (supabase as any).rpc('has_module_access', {
            _user_id: userId,
            _module: module,
          });
          return [module, allowed === true] as const;
        })
      );
      checks.forEach(([module, allowed]) => {
        if (allowed) { map[module] = true; explicit[module] = true; }
      });
    }

    const entry: CacheEntry = { ts: Date.now(), permissions: map, explicit };
    permCache.set(userId, entry);
    permListeners.get(userId)?.forEach(l => l(entry));
    return entry;
  })();

  permInflight.set(userId, p);
  try { return await p; } finally { permInflight.delete(userId); }
}

export function invalidateModulePermissions(userId?: string) {
  if (userId) permCache.delete(userId); else permCache.clear();
}

export function useModulePermissions(userId?: string) {
  const { user, isAdmin } = useAuth();
  const targetUserId = userId || user?.id;
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [explicitPermissions, setExplicitPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!targetUserId) {
      setPermissions({}); setExplicitPermissions({}); setLoading(false);
      return;
    }
    invalidateModulePermissions(targetUserId);
    const entry = await loadPermissions(targetUserId);
    setPermissions(entry.permissions);
    setExplicitPermissions(entry.explicit);
    setLoading(false);
  }, [targetUserId]);

  useEffect(() => {
    if (!targetUserId) {
      setPermissions({}); setExplicitPermissions({}); setLoading(false);
      return;
    }

    const cached = permCache.get(targetUserId);
    if (cached && Date.now() - cached.ts < PERM_TTL_MS) {
      setPermissions(cached.permissions);
      setExplicitPermissions(cached.explicit);
      setLoading(false);
    } else {
      setLoading(true);
      loadPermissions(targetUserId).then((entry) => {
        setPermissions(entry.permissions);
        setExplicitPermissions(entry.explicit);
        setLoading(false);
      });
    }

    const listener: PermListener = (entry) => {
      setPermissions(entry.permissions);
      setExplicitPermissions(entry.explicit);
    };
    if (!permListeners.has(targetUserId)) permListeners.set(targetUserId, new Set());
    permListeners.get(targetUserId)!.add(listener);
    return () => {
      permListeners.get(targetUserId)?.delete(listener);
    };
  }, [targetUserId]);

  const hasAccess = (module: ModuleKey): boolean => {
    if (isAdmin && !userId) return true;
    if (targetUserId) {
      const cached = permCache.get(targetUserId);
      if (cached && Date.now() - cached.ts < PERM_TTL_MS) {
        return cached.permissions[module] === true;
      }
    }
    return permissions[module] === true;
  };

  return { permissions, explicitPermissions, loading, hasAccess, refetch: fetchPermissions };
}
